import { useQuery } from "@tanstack/solid-query";
import type { TovMapEnqueueResponse } from "contracts/api/client-tov/mapping.responses";
import type { TovMapAttemptSummaryResponse } from "contracts/api/client-tov/mapping-attempts";
import { TOV_MAP_ATTEMPT_STATUS } from "contracts/enums/tov-mapping";
import { type Accessor, createEffect } from "solid-js";
import { QUERY_ROOT } from "../../enums/shell";
import { api } from "../../store/api";
import { MAP_ATTEMPT_POLL_MS } from "../../store/pollCadence";
import { queryClient } from "../../store/query";
import { isTovQuery } from "../../store/query-keys";
import { type MappingState, mappingState } from "./mapAttemptState";

/**
 * Latest mapping attempt for a version, polled while it runs. A running
 * attempt discovered on page load resumes polling automatically, and the
 * transition to a terminal state refreshes every tov-* surface once, so the
 * generated draft appears without depending on sources flipping status.
 */
export function createMapAttemptStore(
  clientId: Accessor<string | undefined>,
  versionId: Accessor<string | undefined>,
): {
  summary: Accessor<TovMapAttemptSummaryResponse | null>;
  state: Accessor<MappingState>;
} {
  const query = useQuery(() => ({
    queryKey: [QUERY_ROOT.TOV_MAP_ATTEMPT, clientId(), versionId()],
    queryFn: () =>
      api.tov.latestMapAttempt(clientId() as string, versionId() as string),
    enabled: clientId() !== undefined && versionId() !== undefined,
    refetchInterval: (current) =>
      current.state.data?.attempt?.status === TOV_MAP_ATTEMPT_STATUS.RUNNING
        ? MAP_ATTEMPT_POLL_MS
        : false,
  }));

  let lastStatus: string | undefined;
  createEffect(() => {
    const status = query.data?.attempt?.status;
    if (
      lastStatus === "running" &&
      status !== undefined &&
      status !== TOV_MAP_ATTEMPT_STATUS.RUNNING
    ) {
      void queryClient.invalidateQueries({
        predicate: (candidate) =>
          isTovQuery(candidate.queryKey) &&
          candidate.queryKey[0] !== QUERY_ROOT.TOV_MAP_ATTEMPT,
      });
    }
    lastStatus = status;
  });

  return {
    summary: () => query.data ?? null,
    state: () => mappingState(query.data),
  };
}

/**
 * The one map trigger: enqueues (or resumes) via the existing map endpoint and
 * refreshes the attempt query so polling starts immediately.
 */
export async function requestMapping(
  clientId: string,
  versionId: string,
): Promise<TovMapEnqueueResponse> {
  const result = await api.tov.mapVersion(clientId, versionId);
  await queryClient.invalidateQueries({
    queryKey: [QUERY_ROOT.TOV_MAP_ATTEMPT, clientId, versionId],
  });
  return result;
}
